/* eslint-disable global-require */

const DEFAULT_FILE_ICON = require(
    '@/assets/images/filetype/d_file.png'
);

const FILE_TYPE_ICONS = Object.freeze({
    ai: require('@/assets/images/filetype/ai.png'),
    cpp: require(
        '@/assets/images/filetype/cpp-file-format-symbol.png'
    ),
    css: require('@/assets/images/filetype/css.png'),
    csv: require('@/assets/images/filetype/csv.png'),
    doc: require('@/assets/images/filetype/doc.png'),
    docx: require('@/assets/images/filetype/doc.png'),
    eps: require('@/assets/images/filetype/ai.png'),
    gif: require('@/assets/images/filetype/jpg.png'),
    jpeg: require('@/assets/images/filetype/jpg.png'),
    jpg: require('@/assets/images/filetype/jpg.png'),
    json: require('@/assets/images/filetype/json-file.png'),
    pdf: require('@/assets/images/filetype/pdf.png'),
    png: require('@/assets/images/filetype/png.png'),
    ppt: require('@/assets/images/filetype/ppt.png'),
    pptx: require('@/assets/images/filetype/ppt.png'),
    py: require('@/assets/images/filetype/python.png'),
    rar: require('@/assets/images/filetype/zip-1.png'),
    txt: require('@/assets/images/filetype/txt.png'),
    vb: require('@/assets/images/filetype/coding.png'),
    vbb: require('@/assets/images/filetype/coding.png'),
    xls: require('@/assets/images/filetype/xls.png'),
    xlsm: require('@/assets/images/filetype/xls.png'),
    xlsx: require('@/assets/images/filetype/xls.png'),
    xml: require('@/assets/images/filetype/xml.png'),
    zip: require('@/assets/images/filetype/zip.png')
});

/*
 * SVG is intentionally excluded from inline preview because an
 * untrusted SVG may contain active content. It can still be downloaded.
 */
const PREVIEW_IMAGE_EXTENSIONS = Object.freeze({
    gif: true,
    jpeg: true,
    jpg: true,
    png: true,
    webp: true
});

const ALLOWED_FILE_PROTOCOLS = Object.freeze({
    'blob:': true,
    'http:': true,
    'https:': true
});

const hasOwnProperty = Object.prototype.hasOwnProperty;

/**
 * Gets a filename/path from a string, File object or backend object.
 */
function getFileValue(file) {
    if (typeof file === 'string') {
        return file.trim();
    }

    if (!file || typeof file !== 'object') {
        return '';
    }

    if (typeof file.name === 'string' && file.name.trim()) {
        return file.name.trim();
    }

    if (typeof file.url === 'string' && file.url.trim()) {
        return file.url.trim();
    }

    if (typeof file.path === 'string' && file.path.trim()) {
        return file.path.trim();
    }

    if (typeof file.file === 'string' && file.file.trim()) {
        return file.file.trim();
    }

    return '';
}

/**
 * Gets only a URL value. The File.name property is not treated as a URL.
 */
function getFileUrlValue(file) {
    if (typeof file === 'string') {
        return file.trim();
    }

    if (!file || typeof file !== 'object') {
        return '';
    }

    if (typeof file.url === 'string') {
        return file.url.trim();
    }

    if (typeof file.path === 'string') {
        return file.path.trim();
    }

    if (typeof file.file === 'string') {
        return file.file.trim();
    }

    return '';
}

/**
 * Removes URL query parameters and hash fragments.
 */
export function getCleanFilePath(file) {
    const value = getFileValue(file);

    if (!value) {
        return '';
    }

    const queryIndex = value.indexOf('?');
    const hashIndex = value.indexOf('#');
    let endIndex = value.length;

    if (queryIndex !== -1) {
        endIndex = Math.min(endIndex, queryIndex);
    }

    if (hashIndex !== -1) {
        endIndex = Math.min(endIndex, hashIndex);
    }

    return value.slice(0, endIndex);
}

/**
 * Returns the decoded filename without its directory.
 */
export function getFileName(file) {
    const cleanPath = getCleanFilePath(file)
        .replace(/\\/g, '/');

    const encodedFileName =
        cleanPath.split('/').pop() || '';

    if (!encodedFileName) {
        return '';
    }

    let decodedFileName = encodedFileName;

    try {
        decodedFileName = decodeURIComponent(encodedFileName);
    } catch (error) {
        decodedFileName = encodedFileName;
    }

    // Remove control characters from displayed filenames.
    return decodedFileName
        .replace(/[\u0000-\u001F\u007F]/g, '')
        .trim();
}

/**
 * Returns a normalized extension without the dot.
 */
export function getFileExtension(file) {
    const fileName = getFileName(file);
    const lastDotIndex = fileName.lastIndexOf('.');

    // Prevent ".env" and filenames ending with "." being extensions.
    if (
        lastDotIndex <= 0 ||
        lastDotIndex === fileName.length - 1
    ) {
        return '';
    }

    const extension = fileName
        .slice(lastDotIndex + 1)
        .toLowerCase();

    // Prevent unexpected property names or malformed extensions.
    if (!/^[a-z0-9]{1,12}$/.test(extension)) {
        return '';
    }

    return extension;
}

/**
 * Returns an icon for the supplied file.
 */
export function checkFileExtension(file) {
    const extension = getFileExtension(file);

    if (hasOwnProperty.call(FILE_TYPE_ICONS, extension)) {
        return FILE_TYPE_ICONS[extension];
    }

    return DEFAULT_FILE_ICON;
}

/**
 * Determines whether a file can be displayed as a raster image.
 */
export function isImageFile(file) {
    const extension = getFileExtension(file);

    return hasOwnProperty.call(
        PREVIEW_IMAGE_EXTENSIONS,
        extension
    );
}

/**
 * Resolves a safe file URL.
 * Blocks javascript:, data:, file: and other unsafe protocols.
 */
export function getSafeFileUrl(file) {
    const value = getFileUrlValue(file);

    if (
        !value ||
        typeof window === 'undefined' ||
        typeof document === 'undefined'
    ) {
        return '';
    }

    const anchor = document.createElement('a');

    anchor.href = value;

    const protocol = String(anchor.protocol || '')
        .toLowerCase();

    if (!hasOwnProperty.call(ALLOWED_FILE_PROTOCOLS, protocol)) {
        return '';
    }

    return anchor.href;
}

/**
 * Safely opens a trusted file URL.
 */
export function fileOpenNewTab(file) {
    const safeUrl = getSafeFileUrl(file);

    if (!safeUrl) {
        return false;
    }

    const newWindow = window.open(
        safeUrl,
        '_blank',
        'noopener,noreferrer'
    );

    if (newWindow) {
        newWindow.opener = null;
    }

    return Boolean(newWindow);
}